<!-- src/lib/components/migration/HealthReportStep.svelte -->
<!-- Step 4: Fetch and display the health report for the run -->
<script lang="ts">
  import type { HealthReport } from '../../types.ts';
  import { api } from '../../api.ts';

  interface Props {
    runId: string;
    onNext: (report: HealthReport) => void;
    onBack: () => void;
  }
  let { runId, onNext, onBack }: Props = $props();

  let report = $state<HealthReport | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  $effect(() => {
    void fetchReport();
  });

  async function fetchReport() {
    loading = true;
    error = null;
    try {
      report = await api<HealthReport>(`/api/migrate/health-report/${runId}`);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Failed to load health report.';
    } finally {
      loading = false;
    }
  }

  function gradeColor(grade: string): string {
    if (grade === 'A') return 'var(--ab-purple-bright)';
    if (grade === 'B') return 'oklch(0.72 0.22 165)';
    if (grade === 'C') return 'var(--ab-gold-bright)';
    if (grade === 'D') return 'oklch(0.78 0.20 55)';
    return 'var(--ab-red-bright)';
  }
</script>

<div class="step">
  <h2 class="step-title">Health report</h2>
  <p class="step-hint">Review data quality before importing.</p>

  {#if loading}
    <div class="loading">
      <span class="spinner" aria-hidden="true"></span>
      <span>Analyzing data…</span>
    </div>
  {:else if error}
    <p class="error">{error}</p>
    <div class="actions">
      <button class="btn-secondary" onclick={onBack}>Back</button>
      <button class="btn-primary" onclick={fetchReport}>Retry</button>
    </div>
  {:else if report}
    <div class="report">
      <div class="grade-row">
        <span class="grade" style="color: {gradeColor(report.health_grade)}">{report.health_grade}</span>
        <div class="grade-meta">
          <span class="grade-score">Score: {report.health_score}/100</span>
        </div>
      </div>

      <div class="totals-grid">
        {#each Object.entries(report.totals) as [key, val]}
          <div class="total-cell">
            <span class="total-val">{val}</span>
            <span class="total-key">{key}</span>
          </div>
        {/each}
      </div>

      {#if report.warnings.length > 0}
        <div class="list-section">
          <h3 class="list-title">Warnings</h3>
          <ul class="warn-list">
            {#each report.warnings as w}
              <li>{w}</li>
            {/each}
          </ul>
        </div>
      {/if}

      {#if report.suggestions.length > 0}
        <div class="list-section">
          <h3 class="list-title">Suggestions</h3>
          <ul class="suggest-list">
            {#each report.suggestions as s}
              <li>{s}</li>
            {/each}
          </ul>
        </div>
      {/if}
    </div>

    <div class="actions">
      <button class="btn-secondary" onclick={onBack}>Back</button>
      <button class="btn-primary" onclick={() => report && onNext(report)}>Continue</button>
    </div>
  {/if}
</div>

<style>
.step { display: flex; flex-direction: column; gap: 1rem; }
.step-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--text-primary); }
.step-hint { margin: 0; font-size: 0.82rem; color: var(--text-muted); }

.loading {
  display: flex; align-items: center; gap: 0.6rem;
  color: var(--text-muted); font-size: 0.85rem;
  padding: 1.5rem 0;
}
.error { margin: 0; font-size: 0.78rem; color: var(--ab-red-bright); }

.report { display: flex; flex-direction: column; gap: 1rem; }

.grade-row {
  display: flex; align-items: center; gap: 1rem;
  padding: 1rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 8px;
}
.grade {
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.grade-meta { display: flex; flex-direction: column; gap: 0.25rem; }
.grade-score { font-size: 0.85rem; color: var(--text-secondary); }

.totals-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}
.total-cell {
  display: flex; flex-direction: column; align-items: center; gap: 0.2rem;
  padding: 0.6rem;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 6px;
}
.total-val { font-size: 1.25rem; font-weight: 600; color: var(--text-primary); }
.total-key { font-size: 0.7rem; color: var(--text-muted); text-transform: capitalize; }

.list-section { display: flex; flex-direction: column; gap: 0.4rem; }
.list-title { margin: 0; font-size: 0.78rem; font-weight: 600; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.05em; }

.warn-list, .suggest-list {
  margin: 0; padding: 0 0 0 1rem;
  display: flex; flex-direction: column; gap: 0.25rem;
}
.warn-list li { font-size: 0.78rem; color: var(--ab-gold-bright); }
.suggest-list li { font-size: 0.78rem; color: var(--text-muted); }

.actions { display: flex; gap: 0.5rem; justify-content: flex-end; margin-top: 0.25rem; }

.btn-primary {
  background: oklch(0.72 0.28 310 / 0.12);
  border: 1px solid oklch(0.72 0.28 310 / 0.32);
  color: var(--ab-purple-bright);
  padding: 0.4rem 1.25rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-primary:hover { background: oklch(0.72 0.28 310 / 0.22); }

.btn-secondary {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  color: var(--text-muted);
  padding: 0.4rem 0.875rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-secondary:hover { background: rgba(255,255,255,0.07); }

.spinner {
  display: inline-block;
  width: 14px; height: 14px;
  border: 2px solid rgba(255,255,255,0.15);
  border-top-color: var(--ab-purple-bright);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
